import { useState, useEffect } from "react";
import { CheckIcon, ChevronUpDownIcon, XMarkIcon } from "@heroicons/react/20/solid";
import { Combobox } from "@headlessui/react";
import { faCircleNotch } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Fuse from "fuse.js";
import isEmpty from "lodash/isEmpty";

function classNames(...classes) {
    return classes.filter(Boolean).join(" ");
}

const defaultComponents = {
    Option: ({ active, selected, item }) => (
        <>
            <div className="flex cursor-pointer items-center">
                {item.imageUrl && (
                    <img
                        src={item.imageUrl}
                        alt=""
                        className="h-6 w-6 shrink-0 rounded-full"
                    />
                )}
                <span
                    className={classNames(
                        "truncate",
                        selected && "font-semibold",
                        item.imageUrl && "ml-3"
                    )}
                >
                    {item.title}
                </span>
                {item.subTitle && (
                    <span
                        className={classNames(
                            "ml-2 truncate text-gray-500",
                            active ? "text-gray-200" : "text-gray-500"
                        )}
                    >
                        - {item.subTitle}
                    </span>
                )}
            </div>


        </>
    )
}

const inputClassName = "w-full rounded-md border border-gray-300 bg-white py-2 pl-3 pr-10 shadow-sm focus:border-orange-500 focus:outline-none focus:ring-1 focus:ring-orange-500 sm:text-sm disabled:cursor-not-allowed disabled:bg-gray-50 disabled:text-gray-500 disabled:ring-gray-200"

export default function ComboBox({ className = inputClassName, isLoading, clearable = true, loadingMessage, loadOptions, value, onChange, options = [], placeholder, components = null, labelCallback = (item) => item?.title, input = null, onFocus = null, onBlur = null, searchFields = ['title'], disabled = false }) {
    const mergedComponents = { ...defaultComponents, ...components }
    const [selectedItem, setSelectedItem] = useState(value || null);
    const [filteredOptions, setOptions] = useState(options);

    const handleChange = (item) => {
        setSelectedItem(item);
        if (input) {
            input.onChange(item)
        }
        if (onChange) {
            onChange(item);
        }
    };

    const handleQuery = (query) => {
        if (loadOptions) {
            loadOptions(query, (response) => setOptions(response))
        } else {
            if (query !== '') {
                const fuse = new Fuse(options.map(item => (
                    {
                        ...item,
                        titleNormalized: item.title.normalize("NFD").replace(/\p{Diacritic}/gu, "")
                    })), {
                    threshold: 0.3,
                    keys: [...searchFields, "titleNormalized"],
                });
                let newOptions = fuse.search(query.normalize("NFD").replace(/\p{Diacritic}/gu, "")).map((item) => item.item);
                setOptions(newOptions)
            } else {
                setOptions(options)
            }
        }
    }

    useEffect(() => {
        setOptions(options)
    }, [options ? options.length : 0]);

    useEffect(() => {
        setSelectedItem(value)
    }, [value]);

    return <>
        <Combobox
            as="div"
            by="id"
            value={selectedItem}
            onChange={handleChange}
            nullable={clearable}
            disabled={disabled}
        >
            <div className="relative mt-1">
                <Combobox.Button as="div" className="flex items-center">
                    <Combobox.Input
                        autoComplete="off"
                        className={className}
                        onChange={(event) => handleQuery(event.target.value)}
                        displayValue={labelCallback}
                        placeholder={isLoading && loadingMessage ? loadingMessage : placeholder}

                        onFocus={() => {
                            if (onFocus) {
                                onFocus()
                            }
                        }}
                    />
                    <span className={"absolute right-2"}>
                        {isLoading ? <FontAwesomeIcon icon={faCircleNotch} className={"h-5 w-5 mt-1 mr-1 text-gray-400"} spin /> : <ChevronUpDownIcon
                            className="h-5 w-5 text-gray-400"
                            aria-hidden="true"
                        />}
                    </span>
                    {!isLoading && clearable && selectedItem && <span className={"absolute right-8"} onClick={() => handleChange(null)}>
                        <XMarkIcon
                            className="h-5 w-5 text-gray-400 hover:text-gray-800"
                            aria-hidden="true"
                        />
                    </span>}
                </Combobox.Button>

                {!isEmpty(filteredOptions) && filteredOptions.length > 0 && (
                    <Combobox.Options className="absolute z-10 mt-1 max-h-56 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black/5 focus:outline-none sm:text-sm">
                        {filteredOptions.map((item, index) => (
                            <Combobox.Option
                                key={index}
                                value={item}
                                className={({ active }) =>
                                    classNames(
                                        "relative cursor-default select-none py-2 pl-3 pr-9",
                                        active ? "bg-orange-600 text-white" : "text-gray-900"
                                    )
                                }
                            >
                                {({ active, selected }) => <>
                                    <mergedComponents.Option selected={selected} active={active} key={index} item={item} />
                                    {selected && (
                                        <span
                                            className={classNames(
                                                "absolute inset-y-0 right-0 flex items-center pr-4",
                                                active ? "text-white" : "text-orange-600"
                                            )}
                                        >
                                            <CheckIcon className="h-5 w-5" aria-hidden="true" />
                                        </span>
                                    )}
                                </>}
                            </Combobox.Option>
                        ))}
                    </Combobox.Options>
                )}
            </div>
        </Combobox>
    </>;
}
